import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { popupsApi, splitEmails } from '../../utils/api/popups';
import DraftBody, { DraftBodyHandle } from '../DraftBody';

// ── Re-send a corrected custody notice through an editor (owner 2026-10-03) ──────────────────────
// "所有对外发的邮件都需要弹出编辑页面" — the one-click Resend used to mail the counterparty after a
// confirm box. Now the server prepares exactly what that click sent (same recipients, the correction
// wording, "Re:" on the thread of the notice it replaces), the operator checks and edits To / Cc /
// Subject / Body here, and only Send mails it — as a reply on that same thread.

const inp: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 8,
  border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', alignSelf: 'center' };

export default function ResendNotice({ entryId, onClose, onSent }: { entryId: string; onClose: () => void; onSent: (to: string) => void }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [html, setHtml] = useState('');
  const [thread, setThread] = useState<{ correction: boolean; inReplyTo: string; priorSentAt: string }>({ correction: false, inReplyTo: '', priorSentAt: '' });
  const bodyRef = useRef<DraftBodyHandle | null>(null);

  useEffect(() => {
    popupsApi.trResendPrepare(entryId).then(p => {
      setFrom(p.from || ''); setTo((p.to || []).join(', ')); setCc((p.cc || []).join(', '));
      setSubject(p.subject || ''); setHtml(p.bodyHtml || '');
      setThread({ correction: !!p.correction, inReplyTo: p.inReplyTo || '', priorSentAt: p.priorSentAt || '' });
    }).catch((e: any) => setErr(e?.message || 'Failed to prepare')).finally(() => setLoading(false));
  }, [entryId]);

  const send = async () => {
    if (!splitEmails(to).length) { setErr(t('pop_need_to')); return; }
    if (!subject.trim()) { setErr(t('pop_need_subject')); return; }
    setSending(true); setErr('');
    try {
      const r = await popupsApi.trResendSend(entryId, { to, cc, subject, bodyHtml: bodyRef.current ? bodyRef.current.get() : html });
      onSent(r.to || to); onClose();
    } catch (e: any) { setErr(e?.message || 'Send failed'); setSending(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 10120, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onMouseDown={e => { if (e.target === e.currentTarget && !sending) onClose(); }}>
      <div style={{ width: 820, maxWidth: '100%', maxHeight: '92vh', display: 'flex', flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: 14, overflow: 'hidden', boxShadow: 'var(--shadow-md)' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', fontSize: 15, fontWeight: 800 }}>
          <i className="ti ti-mail-forward" style={{ marginRight: 8, color: 'var(--accent)' }} aria-hidden />{t('pop_tr_title')}
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {loading ? <div style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>{t('pop_loading')}</div> : !err || html ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '64px 1fr', gap: '8px 10px', marginBottom: 12 }}>
                <span style={lbl}>{t('pop_from')}</span><span style={{ fontSize: 13, alignSelf: 'center', color: 'var(--text-secondary)' }}>{from}</span>
                <span style={lbl}>{t('pop_to')}</span><input value={to} onChange={e => setTo(e.target.value)} style={inp} />
                <span style={lbl}>{t('pop_cc')}</span><input value={cc} onChange={e => setCc(e.target.value)} style={inp} />
                <span style={lbl}>{t('pop_subject')}</span><input value={subject} onChange={e => setSubject(e.target.value)} style={inp} />
              </div>
              {(thread.correction || thread.inReplyTo) && (
                <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 8, padding: '8px 11px', marginBottom: 10 }}>
                  <i className="ti ti-arrow-back-up" style={{ marginRight: 6 }} aria-hidden />
                  {thread.correction && thread.priorSentAt ? t('pop_tr_correction', { at: thread.priorSentAt }) : t('pop_tr_reply')}
                </div>
              )}
              {/* The notice as it will arrive; editable. White like the email itself. */}
              <DraftBody ref={bodyRef} html={html} style={{ background: '#fff', color: '#111', minHeight: 260, maxHeight: 'none', fontSize: 14, padding: '14px 18px' }} />
            </>
          ) : null}
          {err && <div style={{ marginTop: 10, fontSize: 12.5, color: '#B42318', background: '#FEF3F2', border: '1px solid #FECDCA', borderRadius: 8, padding: '8px 12px' }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} disabled={sending} style={{ padding: '9px 18px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>{t('cancel')}</button>
          <button onClick={send} disabled={sending || loading || !html} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 20px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: sending ? 'default' : 'pointer', opacity: sending || !html ? 0.7 : 1 }}>
            <i className={`ti ${sending ? 'ti-loader-2' : 'ti-send'}`} style={{ animation: sending ? 'spin .8s linear infinite' : undefined, display: 'inline-block' }} />
            {sending ? t('pop_sending') : t('pop_send')}
          </button>
        </div>
      </div>
    </div>
  );
}
